import { useCallback, useEffect, useState } from 'react'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'

import { TypographyStyles } from '@/styles'
import PlaidLink from '@/shared/plaid/PlaidLink'
import { FinancialAccount } from '@/reducers/finances/financialAccountsReducer'
import { PlaidItem } from '@/reducers/finances/plaidItemReducer'
import { fetchPlaidStatementInstitutionsIfNeeded } from '@/actions/plaidStatementInstitutionActions'
import { selectInstitutionSupportsPlaidStatements } from '@/selectors/financeSelectors'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import {
  hasAutoStatementUploadAccount,
  hasPlaidStatementsDisabledAccount,
  isHealthyInstitution,
} from '../../../helpers'

import { Loader } from '@/base'
import { Alert, Icon, Link, Text } from '@/ui'

const SparklesIcon = ({ className }: { className?: string }) => (
  <Icon icon={solid('sparkles')} className={`w-5 ${className || ''}`} />
)

const Title = ({ className }: { className?: string }) => (
  <Text variant={TypographyStyles.H3} className={className}>
    NEW! Automatic statement uploads
  </Text>
)

const Body = () => (
  <Text variant={TypographyStyles.BODY_SM}>
    Re-login to this financial institution in Plaid to activate.{' '}
    <Link
      newPage
      href="https://support.joinheard.com/hc/en-us/articles/24343313630103/"
      variant="secondary"
    >
      Learn more &#8594;
    </Link>
  </Text>
)

const PlaidButton = ({
  account,
  disconnected,
  className,
}: {
  account: FinancialAccount
  disconnected: boolean
  className?: string
}) => (
  <PlaidLink
    account={account}
    disconnected={disconnected}
    connectButtonText="Re-login in Plaid"
    reconnectButtonText="Re-login in Plaid"
    buttonSize="medium"
    className={className}
  />
)
export const PlaidStatementRelogin = ({
  institution,
  accounts,
  disconnected,
}: {
  institution: PlaidItem
  accounts: FinancialAccount[]
  disconnected: boolean
}) => {
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const dispatch = useAppDispatch()
  const [fetching, setFetching] = useState(true)
  const institutionSupported = useReselector(
    selectInstitutionSupportsPlaidStatements,
    institution.institutionId
  )

  const track = useAnalyticsTrack()

  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchPlaidStatementInstitutionsIfNeeded())
      setFetching(false)
    }

    fetch()
  }, [dispatch])

  const onClose = useCallback(() => {
    track('close plaid statement reauth', {
      institutionId: institution.institutionId,
      institutionName: institution.institutionName,
    })
  }, [track, institution.institutionId, institution.institutionName])

  if (
    isHealthyInstitution(institution.institutionId) ||
    hasAutoStatementUploadAccount(accounts) ||
    hasPlaidStatementsDisabledAccount(accounts) ||
    !institutionSupported
  ) {
    return null
  }

  if (fetching) {
    return <Loader />
  }

  return (
    <Alert
      dismissible
      permanentDismissalKey={`plaidStatementAccountReloginDismissed.${institution.institutionId}`}
      onClose={onClose}
      className="bg-stone"
    >
      {isMobile ? (
        <div className="w-[96%]">
          <div>
            <SparklesIcon className="inline-block align-baseline" />

            <Title className="inline-block max-w-[85%] pl-1.5" />
          </div>

          <div className="p-2">
            <Body />
          </div>

          <PlaidButton
            account={accounts[0]}
            disconnected={disconnected}
            className="w-full"
          />
        </div>
      ) : (
        <div className="flex w-[96%] justify-between items-center">
          <div className="align-middle">
            <SparklesIcon className="pr-2" />
          </div>

          <div className="flex-grow">
            <Title />
            <Body />
          </div>

          <PlaidButton
            account={accounts[0]}
            disconnected={disconnected}
            className="max-w-[160px]"
          />
        </div>
      )}
    </Alert>
  )
}
